/**
 * RÉFÉRENTIELS → REMISES — les plafonds de remise de chaque modèle de droits, et les articles qu'aucune remise n'atteint
 * (maquette, onglet `rem`).
 *
 * ⚠️ UN PLAFOND PAR MODÈLE DE DROITS DU MAGASIN, pas par « rôle » fixe : les modèles créés par le magasin ont les leurs, et
 *    chaque compte suit ceux de son modèle (`docs/CADRAGE-Modeles-Droits-Magasin.md`, D7).
 * ⚠️ LE DÉBLOCAGE EST LE DROIT « Accorder une remise » DU MODÈLE (cadrage, D7) : l'interrupteur l'écrit — Code
 *    responsable quand il est ouvert, Libre quand il est fermé. Interdit, le modèle n'accorde aucune remise, et la carte le dit.
 * ⚠️ « SANS MOTIF » AU-DESSUS D'« AVEC MOTIF » EST REFUSÉ, à l'écran comme au serveur : le dessin gardait le plus bas des
 *    deux, une règle que personne n'aurait écrite.
 * ⚠️ LES CAISSES NE LES LISENT PAS ENCORE (l'APK groupé, tranche 9) : l'onglet le dit en tête, au lieu de laisser croire
 *    qu'un plafond réglé ici borne déjà le comptoir.
 */
import { useCallback, useEffect, useState } from 'react';
import { formatRateBp, parseRateBp } from '@orgazone/core-vat';
import { showToast, Trans } from '@orgazone/ui-web';
import { api, type NonDiscountableView, type RightsTemplateView, type RosterView } from '../api';
import { t } from '../i18n';
import { templateName } from '../rights-templates';
import { FacetError, FacetReadFailure, reasonOf } from './referential-facets-shared';

const CARD = { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)' } as const;
const INPUT = {
  height: 38, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
  color: 'var(--ink-900)', fontSize: 'var(--t-body)', width: '100%', fontFamily: 'var(--font-mono)', textAlign: 'right',
} as const;
const strong = (x: string) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>;

/** La teinte de la pastille d'un modèle — celle de son modèle de l'éditeur, comme dans Opérateurs & droits. */
const TINT: Record<RightsTemplateView['base'], { bg: string; fg: string }> = {
  vend: { bg: 'var(--ink-100)', fg: 'var(--ink-600)' },
  resp: { bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  ger: { bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
};

/** Ce que fera la caisse avec ces plafonds et ce déblocage — la phrase de la carte. */
export function ceilingPhrase(tpl: RightsTemplateView): string {
  const rem = tpl.values.rem;
  if (rem === 'no') return t('Ce modèle n’accorde aucune remise : le droit « Accorder une remise » y est interdit.');
  const free = formatRateBp(tpl.discount.freeBp);
  const max = formatRateBp(tpl.discount.maxBp);
  if (tpl.discount.freeBp === tpl.discount.maxBp) {
    return rem === 'ask'
      ? t('Jusqu’à {max}, la remise passe sans rien justifier. Au-delà, la caisse refuse et propose l’autorisation d’un supérieur.', { max })
      : t('Jusqu’à {max}, la remise passe sans rien justifier. Au-delà, la caisse refuse — personne ne peut débloquer.', { max });
  }
  return rem === 'ask'
    ? t('Jusqu’à {free}, la remise passe sans rien justifier. De {free} à {max}, un motif est obligatoire. Au-delà de {max}, la caisse refuse et propose l’autorisation d’un supérieur.', { free, max })
    : t('Jusqu’à {free}, la remise passe sans rien justifier. De {free} à {max}, un motif est obligatoire. Au-delà de {max}, la caisse refuse — personne ne peut débloquer.', { free, max });
}

/**
 * Les deux plafonds saisis, relus — ou pourquoi on ne les envoie pas.
 * ⚠️ REFUSER, JAMAIS CORRIGER (`parseRateBp`) : « 12,555 » ou « 101 » ne deviennent ni 12,56 ni 100.
 */
export function readCeilings(free: string, max: string): { freeBp: number; maxBp: number } | { error: string } {
  const freeBp = parseRateBp(free);
  const maxBp = parseRateBp(max);
  if (freeBp === null || maxBp === null) return { error: t('Un pourcentage de 0 à 100, deux décimales au plus.') };
  if (freeBp > maxBp) return { error: t('« Sans motif » ne peut pas dépasser « Avec motif » : corrigez l’un des deux.') };
  return { freeBp, maxBp };
}

/** La carte d'un modèle : ses deux plafonds, son déblocage, et ce que la caisse en fera. */
function CeilingCard({ tpl, busy, onSave }: {
  tpl: RightsTemplateView;
  busy: boolean;
  onSave: (tpl: RightsTemplateView, change: { discount?: { freeBp: number; maxBp: number }; rem?: 'ask' | 'yes' }) => Promise<boolean>;
}) {
  const name = templateName(tpl);
  const [free, setFree] = useState(formatRateBp(tpl.discount.freeBp, false));
  const [max, setMax] = useState(formatRateBp(tpl.discount.maxBp, false));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setFree(formatRateBp(tpl.discount.freeBp, false));
    setMax(formatRateBp(tpl.discount.maxBp, false));
  }, [tpl.discount.freeBp, tpl.discount.maxBp]);

  /** Les plafonds partent quand on quitte un champ, s'ils ont changé et se lisent. */
  const commit = () => {
    const read = readCeilings(free, max);
    if ('error' in read) { setError(read.error); return; }
    setError(null);
    if (read.freeBp === tpl.discount.freeBp && read.maxBp === tpl.discount.maxBp) return;
    void onSave(tpl, { discount: read });
  };

  const rem = tpl.values.rem;
  const unlock = rem === 'ask';
  const tint = TINT[tpl.base];
  return (
    <div style={{ ...CARD, padding: '16px 17px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13 }}>
        <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: tint.bg, color: tint.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--t-dense)', fontWeight: 800, flexShrink: 0 }}>
          {name.slice(0, 1).toUpperCase()}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{name}</span>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', flexShrink: 0 }}>{t('{n} opérateur', { n: tpl.accounts })}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label>
          <span style={{ display: 'block', fontSize: 'var(--t-micro)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Sans motif')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input inputMode="decimal" aria-label={t('Sans motif — {modele}', { modele: name })} value={free} disabled={busy}
              onChange={(e) => setFree(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={INPUT} />
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-700)' }}>%</span>
          </span>
        </label>
        <label>
          <span style={{ display: 'block', fontSize: 'var(--t-micro)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Avec motif')}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input inputMode="decimal" aria-label={t('Avec motif — {modele}', { modele: name })} value={max} disabled={busy}
              aria-invalid={error !== null}
              onChange={(e) => setMax(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              style={{ ...INPUT, borderColor: error ? 'var(--rose)' : 'var(--ink-200)' }} />
            <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-700)' }}>%</span>
          </span>
        </label>
      </div>
      {error && <div role="alert" style={{ marginTop: 6, fontSize: 'var(--t-meta)', color: 'var(--rose-fg)', fontWeight: 600 }}>{error}</div>}
      {/* ⚠️ INTERDIT ne se règle pas ici : ouvrir le déblocage poserait « Code responsable » sur un modèle qui n'accorde aucune
          remise — c'est dans Opérateurs & droits que le droit s'ouvre. */}
      <button type="button" role="switch" aria-checked={unlock} disabled={busy || rem === 'no'}
        aria-label={t('Déblocage possible par un supérieur — {modele}', { modele: name })}
        onClick={() => { void onSave(tpl, { rem: unlock ? 'yes' : 'ask' }); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', marginTop: 12, padding: '9px 11px',
          border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', textAlign: 'left',
          cursor: busy || rem === 'no' ? 'not-allowed' : 'pointer', opacity: rem === 'no' ? 0.55 : 1,
        }}>
        <span aria-hidden="true" style={{
          width: 38, height: 22, borderRadius: 'var(--r-pill)', background: unlock ? 'var(--teal-500)' : 'var(--ink-200)', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: unlock ? 'flex-end' : 'flex-start', padding: '0 3px', boxSizing: 'border-box',
        }}><span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} /></span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-700)', lineHeight: 1.4 }}>{t('Déblocage possible par un supérieur')}</span>
      </button>
      <div style={{ marginTop: 12, padding: '12px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', fontSize: 'var(--t-dense)', color: 'var(--ink-700)', lineHeight: 1.6 }}>
        {ceilingPhrase(tpl)}
      </div>
    </div>
  );
}

/** Pourquoi un article n'a pas de remise — la pastille de sa ligne. */
const REASON_TEXT = {
  flagged: () => t('Remises interdites'),
  partly_flagged: () => t('Remises interdites en partie'),
  free_price: () => t('Prix libre'),
} as const;

export function DiscountsTab({ onOpenReasons }: {
  /** Ouvre l'onglet des motifs : les motifs de remise y vivent avec les autres. */
  onOpenReasons: () => void;
}) {
  const [roster, setRoster] = useState<RosterView | null>(null);
  const [articles, setArticles] = useState<NonDiscountableView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(() => {
    // ⚠️ L'ÉCHEC SE DIT : des cartes vides se liraient « aucun plafond », et une liste vide « tout se remise ».
    Promise.all([api.operators(), api.nonDiscountable()])
      .then(([r, a]) => { setRoster(r); setArticles(a); setFailure(null); })
      .catch((e: unknown) => { setRoster(null); setArticles(null); setFailure(reasonOf(e)); });
  }, []);
  useEffect(read, [read]);

  const save = useCallback(async (tpl: RightsTemplateView, change: { discount?: { freeBp: number; maxBp: number }; rem?: 'ask' | 'yes' }) => {
    setBusy(true); setError(null);
    try {
      const r = await api.saveRightsTemplate(tpl.id, {
        ...(change.discount ? { discount: change.discount } : {}),
        ...(change.rem ? { rights: { rem: change.rem } } : {}),
      });
      setRoster(r.roster);
      showToast(change.rem
        ? (change.rem === 'ask' ? t('Au-delà du plafond, un supérieur pourra débloquer.') : t('Au-delà du plafond, la caisse refusera.'))
        : r.accounts > 0 ? t('Plafonds enregistrés — {n} compte les reçoit.', { n: r.accounts }) : t('Plafonds enregistrés.'));
      return true;
    } catch (e) {
      setError(reasonOf(e));
      return false;
    } finally { setBusy(false); }
  }, []);

  if (failure) return <FacetReadFailure failure={failure} />;
  if (!roster || !articles) return <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture du référentiel…')}</p>;

  const hidden = articles.total - articles.rows.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p role="status" style={{ margin: 0, padding: '12px 16px', border: '1px solid var(--amber)', borderRadius: 'var(--r-md)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-body)', fontWeight: 600, lineHeight: 1.5 }}>
        {t('Les caisses ne lisent pas encore ces plafonds : ils s’enregistrent et descendent avec chaque compte, et s’appliqueront avec la prochaine version de la caisse.')}
      </p>
      <FacetError message={error} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(290px,1fr))', gap: 13, alignItems: 'start' }}>
        {roster.templates.map((tpl) => <CeilingCard key={tpl.id} tpl={tpl} busy={busy} onSave={save} />)}
      </div>

      <div style={{ ...CARD, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span aria-hidden="true" style={{ width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'var(--violet-bg)', color: 'var(--violet-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12V4h8l10 10-8 8L3 12zM6.5 8a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0 -3 0" /></svg>
        </span>
        <span style={{ flex: 1, minWidth: 220 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Motifs de remise')}</span>
          <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.45 }}>
            {t('Ils sont rangés avec les autres motifs — écarts, cagnotte, stock — dans un seul référentiel.')}
          </span>
        </span>
        <button type="button" className="btn-ghost" style={{ height: 36 }} onClick={onOpenReasons}>{t('Ouvrir les motifs')}</button>
      </div>

      <div style={{ ...CARD, padding: '17px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <span style={{ flex: 1, minWidth: 160, fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Articles non remisables')}</span>
          <span style={{ height: 24, padding: '0 10px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-meta)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {t('{n} article', { n: articles.total })}
          </span>
        </div>
        {articles.rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Aucun article en vente n’est écarté des remises.')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {articles.rows.map((a) => (
              <div key={a.code} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '9px 0', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-dense)' }}>
                <span style={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', color: 'var(--ink-400)', flexShrink: 0 }}>{a.code}</span>
                <span style={{ height: 20, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                  {REASON_TEXT[a.reason]()}
                </span>
              </div>
            ))}
            {hidden > 0 && (
              <div style={{ paddingTop: 9, fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
                {t('{n} autre article n’est pas montré : la liste montre les premiers par nom.', { n: hidden })}
              </div>
            )}
          </div>
        )}
        <div style={{ marginTop: 13, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.6 }}>
          <Trans k='<1>Remises interdites</1>, sur la fiche article, écarte un article de toute remise — au comptoir, en promotion comme en soldes, même pour un responsable : c’est une règle de catalogue, pas une question de droit. Un article à <2>prix libre</2> n’a pas de prix de référence : un pourcentage n’y retire rien.' parts={[strong, strong]} />
        </div>
      </div>
    </div>
  );
}
